// CommandPalette — the ONE command surface, pure: a `role="listbox"` of results
// and NOTHING ELSE (no input of its own). The single search input lives in the
// top strip's omnibar bar (`role="combobox"`); this surface attaches FLUSH
// beneath it as the continuous lower body of one bordered card. There is EXACTLY
// ONE text input in the whole feature, and it is NOT here — this owns the render
// + the filter, the bar owns the input.
//
// Results are GROUPED by the author-facing kind (Materials / Sources / Exports
// / Conversations — the authoring flow), then the app commands, and the chosen id is
// reported UP. The host owns the query + the active-option cursor (shell state);
// this surface renders + filters the shared query via the pure `commandResults`,
// the SAME function the host calls to size the keyboard cursor — so the bar's
// cursor and this listbox can never disagree on what the rows are or their order.
//
// This surface is CONTENT the host anchors flush under the bar via a Popover, so
// it paints only the card body (no input, no frame beyond the host's continuous
// border) and does NOT own dismissal: Esc and outside-click are the Popover's
// job (focus stays on the bar input). It stays self-contained + props-driven —
// the host owns the open-intent, the anchoring, the query, and the cursor.
//
// Typed, sums-first: a match is a `CommandItem` discriminated by `kind` (never
// an untyped bag), and the kinds render in a fixed `KIND_ORDER`. The whole
// filtering model is the pure `commandResults` (items, query) → the grouped
// matches + their flat id order.
//
// Pure/props-driven: no view state except a DOM-sync effect that scrolls the
// host's active option into view (the standard combobox behaviour) — nothing
// reaches out; every selection is reported via `onSelect`.
//
// A11y — the listbox half of the combobox pattern: the popup is `role="listbox"`
// (id = `listboxId`, the bar's `aria-controls`), options are `role="option"`
// rows (NOT focusable — the bar keeps DOM focus and points here via
// `aria-activedescendant` = `optionId(activeId)`). `aria-selected` marks the
// active row; ArrowUp/Down/Home/End + Enter are the bar input's job. Each row's
// accessible name pairs the title with its kind noun, so kind survives without
// the group heading — colour is never the sole channel (the leading glyph is a
// shape, the heading is a word). A polite live region announces the match count.

import { useLingui } from "@lingui/react/macro";
import { useEffect, useRef, useState } from "react";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { Icon } from "../Icon/Icon.js";
import { Kbd, type KbdPlatform } from "../Kbd/Kbd.js";
import { ScrollArea } from "../ScrollArea/ScrollArea.js";
import {
    type KindResolution,
    leadingRefinement,
    modeAdmits,
    parseOmni,
    type QueryMode,
    shortestUniquePrefix,
} from "../Search/omni.js";
import { evaluate, SEARCH_KINDS } from "../Search/query.js";
import { Text } from "../Text/Text.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import "./CommandPalette.css";

// The author-facing kinds a result can be. A closed union — the discriminant
// on `CommandItem` — so rendering and grouping switch on it exhaustively and
// a new kind breaks compilation until it is handled. `conversation` opens the
// ambient conversation (flattened-object-model-design.md §5.1: `project` left
// this sum when Project retired as a container, `conversation` joined it);
// `command` runs an app command (the "run a command" half of the palette's
// promise — master audit R1/C24); the rest mirror `ResourceKind`
// (Navigation/types.ts) so the palette speaks the same object vocabulary as
// the rail.
export type CommandKind =
    | "material"
    | "source"
    | "export"
    | "conversation"
    | "command"
    // Dev-only tools, a DISTINCT kind from `command` — hidden from the default
    // view and reachable via the `>>` grammar mode (see Search/omni.ts).
    | "dev-command";

// One searchable library entry. Discriminated by `kind`; `id` is the opaque
// label fed back to `onSelect` (the palette never interprets it), `title` is
// what the author reads and what the substring match runs over.
export type CommandItem = {
    readonly id: string;
    readonly title: string;
    readonly kind: CommandKind;
};

// A rendered group: one kind and the matches under it. Only non-empty groups
// are built, so the render never paints an empty heading.
export type CommandGroup = {
    readonly kind: CommandKind;
    readonly items: readonly CommandItem[];
};

// The pure filter model's whole output: the matches bucketed into the fixed
// kind order (only non-empty groups) AND their flat id order (DOM order = the
// arrow-key navigation sequence). The host calls this to size + move the
// cursor; the palette calls it to render — one function, so the two never
// disagree on which rows exist or their order.
export type CommandResults = {
    readonly groups: readonly CommandGroup[];
    readonly flatIds: readonly string[];
};

export type CommandPaletteProps = {
    // The library to search — every result across all kinds.
    readonly items: readonly CommandItem[];
    // The live query, owned by the host (the bar input renders + drives it).
    // This surface never mutates it — it filters + renders the matching rows and
    // offers create-on-zero.
    readonly query: string;
    // The active option id (the bar's aria-activedescendant target), owned by
    // the host cursor. Its row is `aria-selected` and scrolled into view; null
    // ⇒ no active row (the empty state is showing).
    readonly activeId: string | null;
    // The listbox element id — the bar input's `aria-controls`. Shared so the
    // combobox wiring points at exactly this popup.
    readonly listboxId: string;
    // Maps an item id to its option element id — the SAME scheme the host uses
    // for `aria-activedescendant`, so the bar's cursor and these rows agree.
    readonly optionId: (id: string) => string;
    // A row was activated (pointer): the author chose this result. The host
    // navigates and closes. Keyboard Enter is the bar input's job.
    readonly onSelect: (id: string) => void;
    // Enter-on-zero closes the loop back to creation (federated-search.md §4):
    // when a non-empty query matches nothing, the palette offers "Create
    // material '<query>'". The host routes it (opens the create dialog seeded
    // with the title). Absent → no creation offer (the plain empty state).
    readonly onCreateFromQuery?: (title: string) => void;
    // Refill the bar's query with a suggested string (the sigil-DSL ghost/hint
    // rows: clicking a kind option types `@s:` for the user). The host wires
    // this to its query setter. Absent → the discoverability rows render as
    // plain, non-interactive hints.
    readonly onFillQuery?: (raw: string) => void;
    // Which register the ⌘-digit accelerator hints render in (⌘ on mac, Ctrl
    // elsewhere) and which modifier the reveal-on-hold effect watches. Defaults
    // to "mac" (the pre-platform status quo, matching Kbd).
    readonly platform?: KbdPlatform;
};

// The order groups render in — the rail's own order (Sources / Materials /
// Exports — the authoring flow, P16), then Conversations, then the app
// commands (search stays the palette's primary identity; commands trail).
const KIND_ORDER: readonly CommandKind[] = [
    "source",
    "material",
    "export",
    "conversation",
    "command",
    "dev-command",
];

// Pure: the whole filter model, the shared search engine (P17). The raw query
// parses into a typed `SearchQuery` (bare words = fuzzy, `"quoted"` = exact,
// `type:<kind>` narrows), `evaluate` scores + ranks, then the matches bucket
// into the fixed kind order (empty kinds dropped) with their flat id order.
// Empty query ⇒ everything in order; a `type:`-scoped query collapses to the
// one surviving kind. The host and the palette both call this — one truth for
// the rows + their order.
export const commandResults = (
    items: readonly CommandItem[],
    query: string,
): CommandResults => {
    // The sigil layer picks the mode + desugars `@kind:` into a real
    // SearchQuery; `evaluate` scores it unchanged. The MODE gate (`>` = commands
    // only, `@` = drop commands) filters the KIND_ORDER groups — `command` is
    // not a SearchKind, so it can't be a `type:` term and must gate here.
    const { mode, query: parsed } = parseOmni(query);
    const ranked = evaluate(parsed, items).map((scored) => scored.item);
    const groups = KIND_ORDER.filter((kind) => modeAdmits(mode, kind))
        .map((kind) => ({
            kind,
            items: ranked.filter((item) => item.kind === kind),
        }))
        .filter((group) => group.items.length > 0);
    const flatIds = groups.flatMap((group) => group.items.map((i) => i.id));
    return { groups, flatIds };
};

// Pure: the ⌘-accelerator digit (1-based) → the id of that result in the
// current flat order, or null when out of range (fewer than N results). The
// SAME `flatIds` the arrow-key cursor and the rows use, so ⌘N and the visible
// row can never disagree on which result N is. The shell keydown feeds a digit
// 1..9 (only the first nine rows carry a hint); anything past the last result
// no-ops rather than inventing a target — the keyboard twin of the tab
// accelerator's out-of-range rule.
export const commandResultForDigit = (
    flatIds: readonly string[],
    digit: number,
): string | null =>
    Number.isInteger(digit) && digit >= 1 && digit <= flatIds.length
        ? (flatIds[digit - 1] ?? null)
        : null;

// The count of leading rows that carry a ⌘-digit accelerator (1..9). A shared
// constant so the reveal-on-⌘-hold hint and the shell's digit guard agree.
export const COMMAND_DIGIT_LIMIT = 9;

// Per-kind leading glyph — a distinct SHAPE per kind (the non-colour channel).
// material: a page; source: stacked sheets; export: a box with an out-arrow;
// conversation: a speech bubble; command: a prompt chevron ("run this").
const kindGlyph = (kind: CommandKind) => {
    switch (kind) {
        case "material":
            return <Icon name="material" />;
        case "source":
            return <Icon name="source" />;
        case "export":
            return <Icon name="export" />;
        case "conversation":
            return <Icon name="chat" />;
        case "command":
            return <Icon name="command" />;
        // Dev tools get a DISTINCT glyph from product commands (the code marks —
        // "developer"), so the two tiers read apart at a glance in mixed lists.
        case "dev-command":
            return <Icon name="code" />;
    }
};

export const CommandPalette = ({
    items,
    query,
    activeId,
    listboxId,
    optionId,
    onSelect,
    onCreateFromQuery,
    onFillQuery,
    platform = "mac",
}: CommandPaletteProps) => {
    const { t } = useLingui();

    // The sigil layer, derived from the SAME query prop the listbox filters on
    // (parseOmni is total). The mode drives the quiet under-box mode line; the
    // refinement drives the ghost/ambiguity hints. No new prop, no host plumbing
    // — the palette owns its own discoverability chrome.
    const { mode } = parseOmni(query);
    const refinement = leadingRefinement(query);

    // Option elements, so the host's active option can be scrolled into view
    // (the bar keeps DOM focus — options are never focused).
    const rowRefs = useRef(new Map<string, HTMLDivElement>());

    const { groups, flatIds } = commandResults(items, query);
    const hasResults = flatIds.length > 0;
    // The `>>` (dev commands) sigil is advertised in the legend ONLY when dev
    // commands actually exist — so it never shows to end users (a production
    // build ships none), with no new prop or host plumbing.
    const hasDevCommands = items.some((i) => i.kind === "dev-command");

    // Reveal-on-⌘-hold: the ⌘1..⌘9 row hints appear only while the accelerator
    // modifier is held (⌘ on mac, Ctrl elsewhere), so the pattern is
    // discoverable without adding permanent chrome to every row. A DOM-sync
    // view flag (like the scroll effect below): the listeners live only while
    // this surface is mounted (it mounts only when the palette is open).
    const [modHeld, setModHeld] = useState(false);
    useEffect(() => {
        const held = (e: KeyboardEvent): boolean =>
            platform === "mac" ? e.metaKey : e.ctrlKey;
        const sync = (e: KeyboardEvent) => setModHeld(held(e));
        const clear = () => setModHeld(false);
        window.addEventListener("keydown", sync);
        window.addEventListener("keyup", sync);
        // A blur (focus left the window mid-hold) never fires keyup — clear so a
        // stale reveal can't stick.
        window.addEventListener("blur", clear);
        return () => {
            window.removeEventListener("keydown", sync);
            window.removeEventListener("keyup", sync);
            window.removeEventListener("blur", clear);
        };
    }, [platform]);

    // Keep the host's active option visible — the one DOM-sync effect (the
    // standard combobox behaviour), reacting to the cursor the bar moves.
    // `behavior: "auto"` is explicit (not the default-omitted spelling): it
    // pins this to an instant jump regardless of any ancestor CSS that might
    // later set `scroll-behavior: smooth` — keyboard nav must never inherit
    // an animated scroll (the same "instant, not eased" requirement as the
    // aria-selected highlight above).
    useEffect(() => {
        if (activeId !== null)
            rowRefs.current
                .get(activeId)
                ?.scrollIntoView({ block: "nearest", behavior: "auto" });
    }, [activeId]);

    // The author-facing plural heading per kind (grouped label).
    const headingFor = (kind: CommandKind): string => {
        switch (kind) {
            case "material":
                return t`Materials`;
            case "source":
                return t`Sources`;
            case "export":
                return t`Exports`;
            case "conversation":
                return t`Conversations`;
            case "command":
                return t`Commands`;
            case "dev-command":
                return t`Dev commands`;
        }
    };

    // The singular kind noun, folded into each row's accessible name so the
    // kind survives even when the heading is not announced.
    const nounFor = (kind: CommandKind): string => {
        switch (kind) {
            case "material":
                return t`Material`;
            case "source":
                return t`Source`;
            case "export":
                return t({ message: "Export", context: "resource kind" });
            case "conversation":
                return t`Conversation`;
            case "command":
                return t`Command`;
            case "dev-command":
                return t`Dev command`;
        }
    };

    // The quiet mode-line label: the active sigil mode named in words (Commands
    // / Resources), or the resolved kind's plural heading once `@s:` narrows.
    // Nothing is added to the input — this thin line under the bar plus the
    // reshaped result groups are the whole signal (owner: "nothing that takes
    // too much attention").
    const modeLineLabel = (
        m: QueryMode,
        r: KindResolution | undefined,
    ): string => {
        if (m.type === "commands") return t`Commands`;
        if (m.type === "dev_commands") return t`Dev commands`;
        if (r?.type === "resolved") return headingFor(r.kind);
        return t`Resources`;
    };

    // Discoverability gates (all faint, all under the box):
    const showLegend = mode.type === "everything" && query === "";
    const showGhosts = mode.type === "resources" && refinement === undefined;

    // The create-from-query affordance is offered only when a non-empty query
    // matched nothing and the host wired creation (federated-search.md §4). The
    // button is the pointer path; Enter-on-zero is the bar input's job (host).
    const createTitle = query.trim();
    const canCreate =
        onCreateFromQuery !== undefined && createTitle !== "" && !hasResults;

    return (
        <div className="bilda-command-palette">
            {/* No input here — the ONE combobox input is the top strip's omnibar
                bar; this surface is the pure results listbox that attaches flush
                beneath it. */}

            {/* Polite count for AT as the query narrows — not a visible row. */}
            <VisuallyHidden role="status" aria-live="polite">
                {t`${flatIds.length} results`}
            </VisuallyHidden>

            {/* Sigil-DSL mode line — the quiet, low-attention indicator UNDER the
                bar naming the active mode/kind. Nothing is added to the input;
                the reshaped result groups carry the rest of the signal. Hidden
                in the default "everything" mode. `role="status"` announces the
                mode/kind to AT when it changes (the sigil switch is otherwise a
                purely visual cue — UX-eval P3), politely so it never interrupts
                typing; the label only changes on a mode/kind switch, not per
                keystroke, so it stays quiet. */}
            {mode.type !== "everything" ? (
                <div className="bilda-command-palette__mode-line" role="status">
                    <Text as="span" size="xs" tone="dim">
                        {modeLineLabel(mode, refinement)}
                    </Text>
                </div>
            ) : null}

            {/* The invisible DSL made visible, all faint + under the box. */}
            {showLegend ? (
                <div
                    className="bilda-command-palette__legend"
                    role="note"
                    aria-label={t`Search grammar`}
                >
                    <span className="bilda-command-palette__legend-item">
                        <span className="bilda-command-palette__sigil">
                            &gt;
                        </span>
                        <Text as="span" size="xs" tone="dim">
                            {t`commands`}
                        </Text>
                    </span>
                    <span className="bilda-command-palette__legend-item">
                        <span className="bilda-command-palette__sigil">@</span>
                        <Text as="span" size="xs" tone="dim">
                            {t`resources`}
                        </Text>
                    </span>
                    {hasDevCommands ? (
                        <span className="bilda-command-palette__legend-item">
                            <span className="bilda-command-palette__sigil">
                                &gt;&gt;
                            </span>
                            <Text as="span" size="xs" tone="dim">
                                {t`dev`}
                            </Text>
                        </span>
                    ) : null}
                    <span className="bilda-command-palette__legend-item">
                        <Text as="span" size="xs" tone="faint">
                            {t`type to search`}
                        </Text>
                    </span>
                </div>
            ) : null}

            {showGhosts ? (
                // biome-ignore lint/a11y/useSemanticElements: a named group of non-form buttons is only expressible as div[role=group] (the palette precedent).
                <div
                    className="bilda-command-palette__ghosts"
                    role="group"
                    aria-label={t`Filter by kind`}
                >
                    <Text as="span" size="xs" tone="faint">
                        {t`Filter by kind`}
                    </Text>
                    {SEARCH_KINDS.map((kind) => {
                        const prefix = shortestUniquePrefix(kind, SEARCH_KINDS);
                        return (
                            <button
                                key={kind}
                                type="button"
                                className="bilda-command-palette__ghost"
                                onClick={() => onFillQuery?.(`@${prefix}:`)}
                                disabled={onFillQuery === undefined}
                            >
                                <span className="bilda-command-palette__ghost-at">
                                    @
                                </span>
                                <span className="bilda-command-palette__ghost-prefix">
                                    {prefix}
                                </span>
                                <span className="bilda-command-palette__ghost-rest">
                                    {kind.slice(prefix.length)}
                                </span>
                            </button>
                        );
                    })}
                </div>
            ) : null}

            {refinement?.type === "ambiguous" ? (
                <div className="bilda-command-palette__hint-note" role="note">
                    <Text as="span" size="xs" tone="dim">
                        {t`Several kinds match — did you mean:`}
                    </Text>
                    {refinement.candidates.map((kind) => {
                        const prefix = shortestUniquePrefix(kind, SEARCH_KINDS);
                        return (
                            <button
                                key={kind}
                                type="button"
                                className="bilda-command-palette__ghost"
                                onClick={() => onFillQuery?.(`@${prefix}:`)}
                                disabled={onFillQuery === undefined}
                            >
                                <span className="bilda-command-palette__ghost-at">
                                    @
                                </span>
                                <span className="bilda-command-palette__ghost-prefix">
                                    {prefix}
                                </span>
                                <span className="bilda-command-palette__ghost-rest">
                                    {kind.slice(prefix.length)}
                                </span>
                            </button>
                        );
                    })}
                </div>
            ) : null}

            {refinement?.type === "unknown" ? (
                <div className="bilda-command-palette__hint-note" role="note">
                    <Text as="span" size="xs" tone="dim">
                        {t`No such kind — searching all resources.`}
                    </Text>
                </div>
            ) : null}

            {!hasResults ? (
                <EmptyState
                    icon={<Icon name="search" size="lg" />}
                    title={t`No matches`}
                    body={t`Try a different word from a material, source, export, or conversation title.`}
                >
                    {canCreate ? (
                        <button
                            type="button"
                            className="bilda-command-palette__create"
                            onClick={() => onCreateFromQuery?.(createTitle)}
                        >
                            {t`Create material “${createTitle}”`}
                        </button>
                    ) : null}
                </EmptyState>
            ) : (
                <ScrollArea maxHeight="min(60vh, 24rem)">
                    {/* The role="listbox" popup the bar input drives via
                        aria-activedescendant (the WAI-ARIA combobox pattern);
                        listbox has no native element, so no useSemanticElements
                        override is needed here — only the groups + options do. */}
                    <div
                        id={listboxId}
                        role="listbox"
                        aria-label={t`Results`}
                        className="bilda-command-palette__results"
                    >
                        {groups.map((group) => (
                            // biome-ignore lint/a11y/useSemanticElements: role="group" names the kind bucket; <fieldset>/<section> are form/landmark idioms (the Palette precedent).
                            <div
                                key={group.kind}
                                role="group"
                                className="bilda-command-palette__group"
                                aria-label={headingFor(group.kind)}
                            >
                                <div
                                    className="bilda-command-palette__group-head"
                                    aria-hidden="true"
                                >
                                    <Text
                                        as="span"
                                        size="xs"
                                        weight="medium"
                                        tone="faint"
                                    >
                                        {headingFor(group.kind)}
                                    </Text>
                                </div>

                                {group.items.map((item) => {
                                    // The row's 1-based position in the flat
                                    // order — the SAME digit `commandResultForDigit`
                                    // maps back to, so the ⌘N hint and the ⌘N
                                    // activation always name the same row. Only
                                    // the first nine carry a hint.
                                    const digit = flatIds.indexOf(item.id) + 1;
                                    return (
                                        // biome-ignore lint/a11y/useKeyWithClickEvents: the bar input owns keyboard via aria-activedescendant; the option is a pointer target only (Enter is handled on the bar).
                                        <div
                                            key={item.id}
                                            id={optionId(item.id)}
                                            role="option"
                                            // Programmatically focusable (satisfies
                                            // the interactive-role a11y floor) but not
                                            // a tab stop: the bar keeps DOM focus and
                                            // points here via aria-activedescendant.
                                            tabIndex={-1}
                                            aria-selected={item.id === activeId}
                                            ref={(el) => {
                                                const map = rowRefs.current;
                                                if (el) map.set(item.id, el);
                                                else map.delete(item.id);
                                            }}
                                            className="bilda-command-palette__row"
                                            aria-label={`${item.title}, ${nounFor(item.kind)}`}
                                            // Two independent row highlights (VSCode
                                            // quick-open): the keyboard cursor paints
                                            // the accent-selected row (aria-selected),
                                            // and CSS :hover paints a grey row under
                                            // the pointer. Hover is purely visual — it
                                            // does NOT move the active option, so the
                                            // accent can rest on one row while grey
                                            // follows the mouse to another. Click still
                                            // picks this row directly.
                                            onClick={() => onSelect(item.id)}
                                        >
                                            <span
                                                className="bilda-command-palette__row-icon"
                                                aria-hidden="true"
                                            >
                                                {kindGlyph(item.kind)}
                                            </span>
                                            <span className="bilda-command-palette__row-title">
                                                {item.title}
                                            </span>
                                            {modHeld &&
                                            digit <= COMMAND_DIGIT_LIMIT ? (
                                                // Decorative accelerator hint —
                                                // aria-hidden (the digit is never the
                                                // sole channel; the row keeps its full
                                                // accessible name, and the footer
                                                // announces the ⌘N pattern).
                                                <Kbd
                                                    className="bilda-command-palette__row-kbd"
                                                    keys={[
                                                        "mod",
                                                        String(digit),
                                                    ]}
                                                    platform={platform}
                                                    aria-hidden="true"
                                                />
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </ScrollArea>
            )}

            <div className="bilda-command-palette__hints">
                <span className="bilda-command-palette__hint">
                    <Kbd keys={["↑", "↓"]} />
                    <Text size="xs" tone="dim">
                        {t`Navigate`}
                    </Text>
                </span>
                <span className="bilda-command-palette__hint">
                    <Kbd keys={["↵"]} />
                    <Text size="xs" tone="dim">
                        {t`Open`}
                    </Text>
                </span>
                {/* The ⌘-digit accelerator, surfaced as an always-present,
                    screen-reader-legible signifier so the shortcut is never
                    invisible (the per-row ⌘N caps only reveal on ⌘-hold). */}
                <span className="bilda-command-palette__hint">
                    <Kbd keys={["mod", "1"]} platform={platform} />
                    <Text size="xs" tone="dim">
                        {t`Jump to result 1–9`}
                    </Text>
                </span>
                <span className="bilda-command-palette__hint">
                    <Kbd keys={["Esc"]} />
                    <Text size="xs" tone="dim">
                        {t`Close`}
                    </Text>
                </span>
            </div>
        </div>
    );
};
